/**
 * Pick the board's density from the height it actually has — "fill the screen, no scrolling".
 * The floor board fits rows to a table; this one fits panels to a grid, so the test is over the
 * grid's row count rather than the floor count, and the unit is the TALLEST rendered panel because
 * floors are not the same height. It measures out of the DOM and scales the other tiers by
 * {@link RATIO}; why predicting the heights from the class sets instead failed is in
 * `sewing-floor-board/design-notes.md`.
 *
 * **Not a §4.8 violation:** nothing here writes a font size or any other style. The measured numbers
 * choose one of the prebuilt tiers in `tokens.ts` and never become CSS.
 */

import { useEffect, useRef, useState, type RefObject } from 'react';

import { TIER_COLUMNS, type Tier } from './tokens';

/** Panel height of each tier *relative to* `lg`. Only the proportions matter; the scale is measured. */
const RATIO: Record<Tier, number> = { xl: 1.5, lg: 1, md: 0.72, sm: 0.52, desk: 1 };

/** Largest first — the loop takes the first that fits. */
const ORDER: readonly Tier[] = ['xl', 'lg', 'md', 'sm'];

/** Slack, so a board chosen to fit by three pixels does not overflow when a chip wraps. */
const SAFETY_PX = 8;

/**
 * What a panel would be tall if the grid were not stretching it: its content wrapper, unaffected by
 * `1fr` row stretch, plus the panel's own padding and border back on.
 */
function naturalPanelHeight(panel: Element): number {
  const wrapper = panel.firstElementChild;
  if (wrapper === null) {
    return panel.getBoundingClientRect().height;
  }

  const style = window.getComputedStyle(panel);
  return (
    wrapper.getBoundingClientRect().height
    + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom)
    + parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth)
  );
}

/**
 * What one grid row costs: the gap under it, plus the height of the TALLEST panel. Floors hold very
 * different numbers of lines, so the first panel is not the one that constrains the fit.
 */
function panelRowHeight(panels: NodeListOf<Element>): number {
  let tallest = 0;
  for (const panel of panels) {
    tallest = Math.max(tallest, naturalPanelHeight(panel));
  }
  if (!Number.isFinite(tallest) || tallest <= 0) {
    return 0;
  }
  const grid = panels[0]?.parentElement ?? null;
  const gap = grid === null ? NaN : parseFloat(window.getComputedStyle(grid).rowGap);
  return tallest + (Number.isFinite(gap) ? gap : 0);
}

export interface FittedPanels {
  /** Put this on the element that owns the height available to the panel grid — the grid, or a wrapper round it. */
  ref: RefObject<HTMLDivElement | null>;
  tier: Tier;
  /** Panel columns for the chosen tier — `TIER_COLUMNS[tier]`, surfaced so the grid need not import it. */
  columns: number;
}

export function useFittedPanels(kiosk: boolean, floorCount: number): FittedPanels {
  const ref = useRef<HTMLDivElement>(null);
  // `md`, not `xl`: the first paint precedes any measurement, so the correction grows the board.
  const [tier, setTier] = useState<Tier>('md');

  useEffect(() => {
    const element = ref.current;
    if (!kiosk || element === null || floorCount === 0) {
      return;
    }

    // Never measure synchronously in the effect body: ResizeObserver fires once on observe(), so the
    // first measurement arrives here without a cascading render. `tier` is a dependency so it re-measures.
    const observer = new ResizeObserver(() => {
      const available = element.clientHeight;
      // `[data-floor-panel]` is the contract with `panels.tsx`, so the ref may sit on a wrapper.
      const panels = element.querySelectorAll('[data-floor-panel]');
      if (available <= 0 || panels.length === 0) {
        return;
      }

      const natural = panelRowHeight(panels);
      if (!Number.isFinite(natural) || natural <= 0) {
        return;
      }

      // What one "ratio unit" of panel costs, calibrated from the tier the closure holds — the one on screen.
      const unit = natural / RATIO[tier];
      const fits = (candidate: Tier) => {
        const rows = Math.ceil(floorCount / TIER_COLUMNS[candidate]);
        return rows * unit * RATIO[candidate] + SAFETY_PX <= available;
      };

      setTier(ORDER.find(fits) ?? 'sm');
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [kiosk, floorCount, tier]);

  const fitted: Tier = kiosk ? tier : 'desk';
  return { ref, tier: fitted, columns: TIER_COLUMNS[fitted] };
}
